import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Brain, Lock, Mail, User, Loader2, ShieldCheck, ArrowRight, XCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function SetupAdmin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking | available | disabled
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    api.get('/admin/setup-status').then(res => {
      setStatus(res.data.available ? 'available' : 'disabled');
    }).catch(() => setStatus('disabled'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/admin/setup', { name, email, password });
      localStorage.setItem('auth_token', res.data.token);
      setUser(res.data);
      toast.success('Admin account created!');
      navigate('/admin', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="setup-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (status === 'disabled') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="setup-disabled">
        <motion.div className="text-center max-w-md space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary/50">
            <XCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Admin Already Initialized</h2>
          <p className="text-muted-foreground">The admin account has already been set up. Please log in with your admin credentials.</p>
          <Button onClick={() => navigate('/login')} data-testid="setup-go-login">Go to Login</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="setup-admin-page">
      <motion.div className="w-full max-w-md space-y-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Brain className="h-6 w-6 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-semibold tracking-tight">MindFlo</span>
          </div>
          <p className="text-muted-foreground text-sm">First-time setup — create your admin account</p>
        </div>

        <Card className="bg-card border-border/50">
          <CardContent className="p-8">
            <div className="mb-6 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
              <p className="text-xs text-muted-foreground">This setup page is available only once. After the admin account is created, this page will be permanently disabled.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Admin name" className="pl-10" required data-testid="setup-name" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@example.com" className="pl-10" required data-testid="setup-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" className="pl-10" minLength={6} required data-testid="setup-password" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm password" className="pl-10" minLength={6} required data-testid="setup-confirm" />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="setup-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1.5" /> Create Admin Account<ArrowRight className="h-4 w-4 ml-1.5" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
